/**
 * Zod validation schemas for Workforce module
 * Comprehensive validation for Employee, Department, Role, and Payroll entities
 */

import { z } from 'zod'
import { isCPF, isEmail, isPhoneNumber } from '../utils/validators'
import {
  STRING_LENGTHS,
  NUMBER_CONSTRAINTS,
  DATE_CONSTRAINTS,
  PATTERNS,
  ENUMS,
  DEFAULTS
} from './config'

// ============================================================================
// Helper Schemas
// ============================================================================

/**
 * Address schema with optional fields
 */
const addressSchema = z.object({
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zipCode: z.string().optional()
}).optional()

/**
 * Emergency contact schema with optional fields
 */
const emergencyContactSchema = z.object({
  name: z.string().optional(),
  phone: z
    .union([
      z.literal(''),
      z.string().regex(/^\(\d{2}\)\s\d{5}-\d{4}$/, 'Telefone inválido. Use formato brasileiro (11) 98765-4321')
    ])
    .optional(),
  relationship: z.string().optional()
}).optional()

/**
 * Benefits schema with optional boolean flags
 */
const benefitsSchema = z.object({
  healthInsurance: z.boolean().optional(),
  dentalInsurance: z.boolean().optional(),
  visionInsurance: z.boolean().optional(),
  lifeInsurance: z.boolean().optional(),
  retirement401k: z.boolean().optional(),
  paidTimeOff: z.number().nonnegative().optional(),
  mealVoucher: z.number().nonnegative().optional(),
  transportVoucher: z.number().nonnegative().optional(),
  gymMembership: z.boolean().optional()
}).optional()

// ============================================================================
// Employee Schemas
// ============================================================================

/**
 * Personal info schema for employee creation
 */
const personalInfoSchema = z.object({
  name: z.string()
    .min(STRING_LENGTHS.NAME_MIN, `Nome deve ter no mínimo ${STRING_LENGTHS.NAME_MIN} caracteres`)
    .max(STRING_LENGTHS.NAME_MAX, `Nome deve ter no máximo ${STRING_LENGTHS.NAME_MAX} caracteres`),

  phone: z
    .union([
      z.literal(''),
      z.string().regex(/^\(\d{2}\)\s\d{5}-\d{4}$/, 'Telefone inválido. Use formato brasileiro (11) 98765-4321')
    ])
    .optional(),

  document: z.string()
    .refine(isCPF, { message: 'CPF inválido. Verifique o formato e dígitos verificadores' })
    .optional(),

  birthDate: z.union([
    z.string().regex(PATTERNS.ISO_DATE, 'Data inválida'),
    z.null()
  ]).optional(),

  address: addressSchema,

  emergencyContact: emergencyContactSchema
})

/**
 * Employment info schema for employee creation
 */
const employmentSchema = z.object({
  departmentId: z.number()
    .positive('Departamento é obrigatório'),

  roleId: z.number()
    .positive('Cargo é obrigatório'),

  managerId: z.union([
    z.string().uuid('ID de gestor inválido'),
    z.null()
  ]).optional(),

  startDate: z.string()
    .regex(PATTERNS.ISO_DATE, 'Data de início inválida')
    .refine((date) => {
      const startDate = new Date(date)
      return startDate >= DATE_CONSTRAINTS.MIN_DATE
    }, `Data de início deve ser posterior a ${DATE_CONSTRAINTS.MIN_YEAR}`)
    .refine((date) => {
      const startDate = new Date(date)
      return startDate <= DATE_CONSTRAINTS.MAX_DATE
    }, 'Data de início não pode ser no futuro'),

  endDate: z.union([
    z.string().regex(PATTERNS.ISO_DATE, 'Data de término inválida'),
    z.null()
  ]).optional(),

  status: z.enum(ENUMS.EMPLOYEE_STATUSES, {
    message: 'Status inválido. Opções: ACTIVE, INACTIVE, PENDING, TERMINATED'
  }).default(DEFAULTS.EMPLOYEE_STATUS),

  salary: z.number()
    .positive('Salário deve ser maior que zero')
    .max(NUMBER_CONSTRAINTS.SALARY_MAX, `Salário deve ser menor que R$ ${NUMBER_CONSTRAINTS.SALARY_MAX.toLocaleString('pt-BR')}`)
    .optional(),

  contractType: z.enum(ENUMS.CONTRACT_TYPES, {
    message: 'Tipo de contrato inválido. Opções: CLT, PJ, FREELANCER, INTERN'
  })
})

/**
 * Create employee schema - supports both nested and flat structure
 */
export const createEmployeeSchema = z.object({
  // Nested structure (preferred)
  personalInfo: personalInfoSchema.optional(),
  employment: employmentSchema.optional(),

  // Flat structure (for form compatibility)
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string()
    .refine(isEmail, { message: 'E-mail inválido' })
    .optional(),
  phone: z.string()
    .regex(PATTERNS.PHONE, 'Telefone inválido. Deve conter 10-15 dígitos')
    .optional(),
  cpf: z.string()
    .refine(isCPF, { message: 'CPF inválido' })
    .optional(),
  departmentId: z.union([z.number().positive(), z.string()]).optional(),
  roleId: z.union([z.number().positive(), z.string()]).optional(),
  hireDate: z.union([z.date(), z.string()]).optional(),
  salary: z.number()
    .positive('Salário deve ser maior que zero')
    .max(NUMBER_CONSTRAINTS.SALARY_MAX)
    .optional(),
  address: addressSchema,
  emergencyContact: emergencyContactSchema,
  benefits: benefitsSchema
}).refine(
  (data) => {
    // Must have either nested structure OR flat structure
    const hasNestedStructure = data.personalInfo && data.employment
    const hasFlatStructure = data.email && (data.departmentId || data.roleId)
    return hasNestedStructure || hasFlatStructure
  },
  { message: 'Dados do funcionário incompletos. Forneça informações pessoais e de emprego' }
)

/**
 * Update employee schema - partial updates allowed
 */
export const updateEmployeeSchema = createEmployeeSchema.partial()

/**
 * TypeScript type inference
 */
export type CreateEmployeeInput = z.infer<typeof createEmployeeSchema>
export type UpdateEmployeeInput = z.infer<typeof updateEmployeeSchema>

// ============================================================================
// Department Schemas
// ============================================================================

/**
 * Create department schema
 */
export const createDepartmentSchema = z.object({
  name: z.string()
    .min(STRING_LENGTHS.NAME_MIN, `Nome deve ter no mínimo ${STRING_LENGTHS.NAME_MIN} caracteres`)
    .max(STRING_LENGTHS.NAME_MAX, `Nome deve ter no máximo ${STRING_LENGTHS.NAME_MAX} caracteres`)
    .regex(PATTERNS.ALPHANUMERIC_EXTENDED, 'Nome deve conter apenas letras, números, espaços e hífens'),

  description: z.string()
    .max(STRING_LENGTHS.DESCRIPTION_MAX, `Descrição deve ter no máximo ${STRING_LENGTHS.DESCRIPTION_MAX} caracteres`)
    .optional(),

  managerId: z.union([
    z.string().uuid('ID de gestor inválido'),
    z.null(),
    z.undefined()
  ]).optional(),

  parentDepartmentId: z.union([
    z.string().uuid('ID de departamento pai inválido'),
    z.null(),
    z.undefined()
  ]).optional(),

  budget: z.union([
    z.number()
      .nonnegative('Orçamento deve ser positivo')
      .max(NUMBER_CONSTRAINTS.BUDGET_MAX, `Orçamento deve ser menor que R$ ${NUMBER_CONSTRAINTS.BUDGET_MAX.toLocaleString('pt-BR')}`),
    z.null()
  ]).optional(),

  costCenter: z.string()
    .regex(PATTERNS.ALPHANUMERIC, 'Centro de custo deve ser alfanumérico')
    .min(STRING_LENGTHS.COST_CENTER_MIN, `Centro de custo deve ter no mínimo ${STRING_LENGTHS.COST_CENTER_MIN} caracteres`)
    .max(STRING_LENGTHS.COST_CENTER_MAX, `Centro de custo deve ter no máximo ${STRING_LENGTHS.COST_CENTER_MAX} caracteres`)
    .optional()
})

/**
 * Update department schema - partial updates allowed
 */
export const updateDepartmentSchema = createDepartmentSchema.partial()

/**
 * TypeScript type inference
 */
export type CreateDepartmentInput = z.infer<typeof createDepartmentSchema>
export type UpdateDepartmentInput = z.infer<typeof updateDepartmentSchema>

// ============================================================================
// Role Schemas
// ============================================================================

/**
 * Salary range schema with cross-field validation
 */
const salaryRangeSchema = z.object({
  min: z.number()
    .positive('Salário mínimo deve ser maior que zero'),

  max: z.number()
    .positive('Salário máximo deve ser maior que zero'),

  currency: z.string()
    .length(3, 'Código de moeda deve ter 3 caracteres')
    .default(DEFAULTS.CURRENCY)
}).refine(
  (data) => data.max > data.min,
  {
    message: 'Salário máximo deve ser maior que o salário mínimo',
    path: ['max']
  }
)

/**
 * Create role schema
 */
export const createRoleSchema = z.object({
  title: z.string()
    .min(STRING_LENGTHS.NAME_MIN, `Título deve ter no mínimo ${STRING_LENGTHS.NAME_MIN} caracteres`)
    .max(STRING_LENGTHS.NAME_MAX, `Título deve ter no máximo ${STRING_LENGTHS.NAME_MAX} caracteres`),

  description: z.string()
    .max(STRING_LENGTHS.LONG_DESCRIPTION_MAX, `Descrição deve ter no máximo ${STRING_LENGTHS.LONG_DESCRIPTION_MAX} caracteres`)
    .optional(),

  departmentId: z.number()
    .positive('Departamento é obrigatório')
    .optional(),

  level: z.enum(ENUMS.ROLE_LEVELS, {
    message: 'Nível inválido. Opções: junior, mid, senior, lead, manager, director, executive'
  }),

  salaryRange: salaryRangeSchema.optional(),

  responsibilities: z.array(z.string()).optional(),

  requirements: z.array(z.string()).optional()
})

/**
 * Update role schema - partial updates allowed
 */
export const updateRoleSchema = createRoleSchema.partial()

/**
 * TypeScript type inference
 */
export type CreateRoleInput = z.infer<typeof createRoleSchema>
export type UpdateRoleInput = z.infer<typeof updateRoleSchema>

// ============================================================================
// Payroll Schemas
// ============================================================================

/**
 * Taxes schema for payroll
 */
const taxesSchema = z.object({
  federal: z.number().nonnegative('Imposto federal deve ser não-negativo'),
  state: z.number().nonnegative('Imposto estadual deve ser não-negativo'),
  local: z.number().nonnegative('Imposto municipal deve ser não-negativo'),
  social: z.number().nonnegative('Contribuição social deve ser não-negativa')
}).optional()

/**
 * Deductions schema for payroll
 */
const deductionsSchema = z.object({
  healthInsurance: z.number().nonnegative('Dedução de plano de saúde deve ser não-negativa'),
  dentalInsurance: z.number().nonnegative('Dedução de plano dentário deve ser não-negativa'),
  retirement: z.number().nonnegative('Dedução de previdência deve ser não-negativa'),
  other: z.number().nonnegative('Outras deduções devem ser não-negativas')
}).optional()

/**
 * Create payroll cost schema
 */
export const createPayrollCostSchema = z.object({
  employeeId: z.string()
    .min(1, 'ID do funcionário é obrigatório'),

  period: z.string()
    .regex(PATTERNS.PERIOD_FORMAT, 'Período inválido. Use formato AAAA-MM (ex: 2024-01)'),

  type: z.enum(ENUMS.PAYROLL_TYPES, {
    message: 'Tipo inválido. Opções: salary, bonus, overtime, commission, reimbursement, deduction, tax, benefit'
  }),

  category: z.string()
    .max(STRING_LENGTHS.CATEGORY_MAX, `Categoria deve ter no máximo ${STRING_LENGTHS.CATEGORY_MAX} caracteres`)
    .optional(),

  description: z.string()
    .max(STRING_LENGTHS.DESCRIPTION_MAX, `Descrição deve ter no máximo ${STRING_LENGTHS.DESCRIPTION_MAX} caracteres`)
    .optional(),

  amount: z.number()
    .min(NUMBER_CONSTRAINTS.PAYROLL_AMOUNT_MIN, `Valor deve ser maior que R$ ${NUMBER_CONSTRAINTS.PAYROLL_AMOUNT_MIN.toLocaleString('pt-BR')}`)
    .max(NUMBER_CONSTRAINTS.PAYROLL_AMOUNT_MAX, `Valor deve ser menor que R$ ${NUMBER_CONSTRAINTS.PAYROLL_AMOUNT_MAX.toLocaleString('pt-BR')}`),

  currency: z.string()
    .length(3, 'Código de moeda deve ter 3 caracteres')
    .default(DEFAULTS.CURRENCY),

  grossAmount: z.number()
    .nonnegative('Valor bruto deve ser não-negativo')
    .optional(),

  netAmount: z.number()
    .nonnegative('Valor líquido deve ser não-negativo')
    .optional(),

  taxes: taxesSchema,

  deductions: deductionsSchema,

  payDate: z.string()
    .regex(PATTERNS.ISO_DATE, 'Data de pagamento inválida')
    .optional()
})

/**
 * TypeScript type inference
 */
export type CreatePayrollCostInput = z.infer<typeof createPayrollCostSchema>

// ============================================================================
// Dialog-Specific Schemas
// ============================================================================

/**
 * Salary change schema
 */
export const salaryChangeSchema = z.object({
  newSalary: z.number()
    .positive('Novo salário deve ser maior que zero')
    .max(NUMBER_CONSTRAINTS.SALARY_MAX, `Salário deve ser menor que R$ ${NUMBER_CONSTRAINTS.SALARY_MAX.toLocaleString('pt-BR')}`),

  reason: z.string()
    .max(STRING_LENGTHS.REASON_MAX, `Motivo deve ter no máximo ${STRING_LENGTHS.REASON_MAX} caracteres`)
    .optional()
})

export type SalaryChangeInput = z.infer<typeof salaryChangeSchema>

/**
 * Terminate employee schema
 */
export const terminateEmployeeSchema = z.object({
  terminationDate: z.string()
    .regex(PATTERNS.ISO_DATE, 'Data de término inválida')
    .refine((date) => {
      const termDate = new Date(date)
      return termDate <= DATE_CONSTRAINTS.MAX_DATE
    }, 'Data de término não pode ser no futuro'),

  reason: z.string()
    .min(STRING_LENGTHS.REASON_MIN, `Motivo deve ter no mínimo ${STRING_LENGTHS.REASON_MIN} caracteres`)
    .max(STRING_LENGTHS.REASON_MAX, `Motivo deve ter no máximo ${STRING_LENGTHS.REASON_MAX} caracteres`)
})

export type TerminateEmployeeInput = z.infer<typeof terminateEmployeeSchema>

/**
 * Change status schema
 */
export const changeStatusSchema = z.object({
  newStatus: z.enum(ENUMS.EMPLOYEE_STATUSES, {
    message: 'Status inválido. Opções: ACTIVE, INACTIVE, PENDING, TERMINATED'
  }),

  effectiveDate: z.string()
    .regex(PATTERNS.ISO_DATE, 'Data efetiva inválida')
    .refine((date) => {
      const effDate = new Date(date)
      return effDate <= DATE_CONSTRAINTS.MAX_DATE
    }, 'Data efetiva não pode ser no futuro')
    .optional()
})

export type ChangeStatusInput = z.infer<typeof changeStatusSchema>

/**
 * Reassign manager schema
 */
export const reassignManagerSchema = z.object({
  newManagerId: z.string()
    .uuid('ID de gestor inválido')
    .min(1, 'Novo gestor é obrigatório'),

  effectiveDate: z.string()
    .regex(PATTERNS.ISO_DATE, 'Data efetiva inválida')
    .optional()
})

export type ReassignManagerInput = z.infer<typeof reassignManagerSchema>
